import { useState, useEffect, useRef } from 'react'
import { X, CheckCircle, XCircle, Loader } from 'lucide-react'
import CopyButton from './CopyButton'
import './WebhooksModal.css'

interface WebhooksModalProps {
  appId: string
  deploymentUrl: string | null
  prEnabled?: boolean
  onClose: () => void
}

type ProbeStatus = 'idle' | 'loading' | 'live' | 'error'

type SecretStatus = 'loading' | 'ready' | 'unauthorized' | 'error'

interface UnkeyedSender {
  at: string
  referrer: string | null
  user_agent: string | null
  source_ip: string | null
}

interface BugWebhook {
  url: string
  key: string
  enforced_at: string | null
  enforces_at: string | null
  last_unkeyed: UnkeyedSender | null
  last_rejected: UnkeyedSender | null
}

function describeSender(sender: UnkeyedSender): string {
  const who = [sender.referrer, sender.user_agent, sender.source_ip].filter(Boolean).join(' · ') || 'unknown sender'
  return `${new Date(sender.at).toLocaleString()} — ${who}`
}

const hasChatSecret = (d: { secret?: string }) => !!d.secret
const hasBugWebhookKey = (d: BugWebhook) => !!d.key

/** Fetch an editor-only credential; 401/403 → 'unauthorized'. */
function useEditorSecret<T>(url: string, isReady: (data: T) => boolean): { status: SecretStatus; data: T | null } {
  const [state, setState] = useState<{ status: SecretStatus; data: T | null }>({ status: 'loading', data: null })
  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading', data: null })
    fetch(url)
      .then(async (res) => {
        if (cancelled) return
        if (res.ok) {
          const data = await res.json() as T
          setState({ status: isReady(data) ? 'ready' : 'error', data })
        } else if (res.status === 401 || res.status === 403) {
          setState({ status: 'unauthorized', data: null })
        } else {
          setState({ status: 'error', data: null })
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', data: null })
      })
    return () => {
      cancelled = true
    }
  }, [url, isReady])
  return state
}

function WebhooksModal({ appId, deploymentUrl, prEnabled, onClose }: WebhooksModalProps) {
  const [probeStatus, setProbeStatus] = useState<ProbeStatus>('idle')
  const [probeError, setProbeError] = useState<string | null>(null)
  const chat = useEditorSecret<{ secret?: string }>(`/api/chat-webhook-secret/${appId}`, hasChatSecret)
  const chatSecret = chat.data?.secret ?? null
  const secretStatus = chat.status
  const bug = useEditorSecret<BugWebhook>(`/api/v1/apps/${appId}/bug-webhook`, hasBugWebhookKey)
  const bugWebhook = bug.status === 'ready' ? bug.data : null
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Until the key loads (or when the viewer isn't an editor) URL and example show a placeholder.
  const bugWebhookUrl = bugWebhook?.url ?? `${window.location.origin}/api/bug-report-webhook/${appId}?key=<WEBHOOK_KEY>`
  const bugCurlExample = `curl -X POST "${bugWebhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '{"body": "Description of the bug", "referrer": "manual", "branch": "my-feature-branch", "callback_url": "https://example.com/webhook"}'`

  const startChatWebhookUrl = `${window.location.origin}/api/start-chat-webhook/${appId}`
  const startChatBody = prEnabled
    ? `{"message": "Describe the change you want", "branch": "my-feature-branch"}`
    : `{"message": "Describe the change you want"}`
  // The Authorization header carries the per-app secret. Until it loads (or when
  // the viewer isn't an editor) the example shows a placeholder to copy over.
  const secretForExample = chatSecret ?? '<APP_SECRET>'
  const startChatCurlExample = `curl -X POST ${startChatWebhookUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${secretForExample}" \\
  -d '${startChatBody}'`

  const endpointPath = '/.netlify/functions/backend-request-recording'
  const recordingEndpointUrl = deploymentUrl
    ? `${deploymentUrl.replace(/\/$/, '')}${endpointPath}`
    : null

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    modalRef.current?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose])

  useEffect(() => {
    if (recordingEndpointUrl) probeEndpoint()
  }, [])

  async function probeEndpoint() {
    if (!recordingEndpointUrl) return
    setProbeStatus('loading')
    setProbeError(null)
    try {
      const probeUrl = `${recordingEndpointUrl}?requestId=00000000-0000-0000-0000-000000000000`
      const res = await fetch('/api/probe-endpoint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: probeUrl }),
      })
      const data = await res.json() as { status?: number; body?: string; error?: string }
      if (!res.ok) {
        setProbeStatus('error')
        setProbeError(data.error || `Proxy error: HTTP ${res.status}`)
      } else if (data.status === 200 || data.status === 404) {
        setProbeStatus('live')
      } else {
        setProbeStatus('error')
        setProbeError(`HTTP ${data.status}`)
      }
    } catch (err) {
      setProbeStatus('error')
      setProbeError((err as Error).message || 'Network error')
    }
  }

  return (
    <div className="webhooks-modal-overlay" data-testid="webhooks-modal-overlay" onClick={onClose}>
      <div
        className="webhooks-modal"
        data-testid="webhooks-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="webhooks-modal-title"
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="webhooks-modal__header">
          <div>
            <h2 className="webhooks-modal__title" id="webhooks-modal-title" data-testid="webhooks-modal-title">Webhooks</h2>
            <p className="webhooks-modal__subtitle">Endpoints for integrating with this app</p>
          </div>
          <button
            className="webhooks-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
            data-testid="webhooks-modal-close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="webhooks-modal__body">
          <div className="webhooks-modal__group" data-testid="webhooks-bug-report-section">
            <h3 className="webhooks-modal__group-title">Bug Report Webhook</h3>
            <p className="webhooks-modal__description">Submit bug reports programmatically via HTTP</p>
            <div className="webhooks-modal__endpoint">
              <div className="webhooks-modal__endpoint-header">
                <span className="webhooks-modal__method">POST</span>
                <span className="webhooks-modal__path">/api/bug-report-webhook/{appId}</span>
                <CopyButton text={bugWebhookUrl} label="Copy URL" data-testid="webhooks-bug-copy-url" />
              </div>
              <div className="webhooks-modal__endpoint-body">
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__label">Authentication</div>
                  <p className="webhooks-modal__description">
                    Send this app&rsquo;s webhook key as <code>?key=&lt;key&gt;</code> (included in the copied URL) or{' '}
                    <code>Authorization: Bearer &lt;key&gt;</code>. App deploys receive the keyed URL as{' '}
                    <code>bug_webhook_url</code>. Keep it private &mdash; anyone with it can start bug-fix agents for this app.
                  </p>
                  {bugWebhook && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-bug-key-enforcement">
                      {bugWebhook.enforced_at
                        ? `Key required since ${new Date(bugWebhook.enforced_at).toLocaleString()}.`
                        : bugWebhook.enforces_at && `Key not required yet. Required from ${new Date(bugWebhook.enforces_at).toLocaleString()} unless a request without it arrives first.`}
                    </div>
                  )}
                  {bugWebhook?.last_unkeyed && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-bug-last-unkeyed">
                      Last accepted request without the key: {describeSender(bugWebhook.last_unkeyed)}
                    </div>
                  )}
                  {bugWebhook?.last_rejected && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-bug-last-rejected">
                      Last rejected request without the key: {describeSender(bugWebhook.last_rejected)}
                    </div>
                  )}
                  {bug.status === 'loading' && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-bug-key-loading">
                      Loading key&hellip;
                    </div>
                  )}
                  {bugWebhook && (
                    <div className="webhooks-modal__secret-row">
                      <code className="webhooks-modal__code-block" data-testid="webhooks-bug-key">{bugWebhook.key}</code>
                      <CopyButton text={bugWebhook.key} label="Copy key" data-testid="webhooks-bug-copy-key" />
                    </div>
                  )}
                  {bug.status === 'unauthorized' && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-bug-key-unauthorized">
                      Sign in as an editor of this app to view its key.
                    </div>
                  )}
                  {bug.status === 'error' && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-bug-key-error">
                      Could not load the key. Close and reopen this dialog to retry.
                    </div>
                  )}
                </div>
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__label">Required Fields</div>
                  <ul className="webhooks-modal__fields">
                    <li><code>body</code> (string) — Description of the bug to report</li>
                  </ul>
                </div>
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__label">Optional Fields</div>
                  <ul className="webhooks-modal__fields">
                    <li><code>referrer</code> (string) — Source label (e.g. "manual", "auto-qa"); echoed back as <code>external_ref</code> in the completion callback</li>
                    <li><code>branch</code> (string) — Target branch the fix container clones and pushes to (PUSH_BRANCH)</li>
                    <li><code>callback_url</code> (string) — URL to receive a POST when the fix task completes</li>
                  </ul>
                </div>
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__curl-header">
                    <div className="webhooks-modal__label">Example</div>
                    <CopyButton text={bugCurlExample} data-testid="webhooks-bug-copy-curl" />
                  </div>
                  <pre className="webhooks-modal__code-block">{bugCurlExample}</pre>
                </div>
              </div>
            </div>
          </div>

          <div className="webhooks-modal__group" data-testid="webhooks-start-chat-section">
            <h3 className="webhooks-modal__group-title">Start Chat Webhook</h3>
            <p className="webhooks-modal__description">
              Start a chat container that does a single turn on a message. The assistant response
              arrives asynchronously &mdash; poll <code>GET /api/chat/{appId}/&lt;chatId&gt;</code> to read it.
            </p>
            <div className="webhooks-modal__endpoint">
              <div className="webhooks-modal__endpoint-header">
                <span className="webhooks-modal__method">POST</span>
                <span className="webhooks-modal__path">/api/start-chat-webhook/{appId}</span>
                <CopyButton text={startChatWebhookUrl} label="Copy URL" data-testid="webhooks-start-chat-copy-url" />
              </div>
              <div className="webhooks-modal__endpoint-body">
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__label">Authentication</div>
                  <p className="webhooks-modal__description">
                    Send this app's ChatWebhookSecret (<code>csk_…</code>) as{' '}
                    <code>Authorization: Bearer &lt;secret&gt;</code>.
                    It authenticates <strong>only</strong> this start-chat webhook — not MCP or{' '}
                    <code>/api/v1</code> (use an <code>lbak_…</code> API key for those).
                    Keep it private &mdash; anyone with it can start chats for this app.
                  </p>
                  {secretStatus === 'loading' && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-start-chat-secret-loading">
                      Loading secret&hellip;
                    </div>
                  )}
                  {secretStatus === 'ready' && chatSecret && (
                    <div className="webhooks-modal__secret-row">
                      <code className="webhooks-modal__code-block" data-testid="webhooks-start-chat-secret">{chatSecret}</code>
                      <CopyButton text={chatSecret} label="Copy secret" data-testid="webhooks-start-chat-copy-secret" />
                    </div>
                  )}
                  {secretStatus === 'unauthorized' && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-start-chat-secret-unauthorized">
                      Sign in as an editor of this app to view its secret.
                    </div>
                  )}
                  {secretStatus === 'error' && (
                    <div className="webhooks-modal__secret-note" data-testid="webhooks-start-chat-secret-error">
                      Could not load the secret. Close and reopen this dialog to retry.
                    </div>
                  )}
                </div>
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__label">Required Fields</div>
                  <ul className="webhooks-modal__fields">
                    <li><code>message</code> (string) &mdash; The message to send to the chat</li>
                  </ul>
                </div>
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__label">Optional Fields</div>
                  <ul className="webhooks-modal__fields">
                    <li>
                      <code>branch</code> (string) &mdash; For PR-based apps, the branch the chat
                      works on and opens a pull request for. Auto-generated when omitted.
                    </li>
                    <li><code>agent</code> (string) &mdash; Coding agent to run: <code>claude</code> (default), <code>codex</code>, or <code>pi</code></li>
                  </ul>
                </div>
                <div className="webhooks-modal__field-group">
                  <div className="webhooks-modal__curl-header">
                    <div className="webhooks-modal__label">Example</div>
                    <CopyButton text={startChatCurlExample} data-testid="webhooks-start-chat-copy-curl" />
                  </div>
                  <pre className="webhooks-modal__code-block">{startChatCurlExample}</pre>
                </div>
              </div>
            </div>
          </div>

          {recordingEndpointUrl && (
            <div className="webhooks-modal__group" data-testid="webhooks-recording-section">
              <div className="webhooks-modal__group-header">
                <h3 className="webhooks-modal__group-title">Backend Recording</h3>
                <div className="webhooks-modal__status" data-testid="webhooks-endpoint-status">
                  {probeStatus === 'loading' && (
                    <span className="webhooks-modal__status-badge webhooks-modal__status-badge--loading">
                      <Loader size={14} className="webhooks-modal__spinner" aria-hidden="true" />
                      Checking...
                    </span>
                  )}
                  {probeStatus === 'live' && (
                    <span className="webhooks-modal__status-badge webhooks-modal__status-badge--live">
                      <CheckCircle size={14} aria-hidden="true" />
                      Live
                    </span>
                  )}
                  {probeStatus === 'error' && (
                    <span className="webhooks-modal__status-badge webhooks-modal__status-badge--error">
                      <XCircle size={14} aria-hidden="true" />
                      Unreachable{probeError ? `: ${probeError}` : ''}
                    </span>
                  )}
                  {probeStatus !== 'loading' && (
                    <button className="webhooks-modal__retry" onClick={probeEndpoint} data-testid="webhooks-retry">
                      Retry
                    </button>
                  )}
                </div>
              </div>
              <p className="webhooks-modal__description">
                Create Replay recordings of backend request executions
              </p>

              <div className="webhooks-modal__section">
                <h4 className="webhooks-modal__section-title">Endpoint URL</h4>
                <code className="webhooks-modal__code-block" data-testid="webhooks-endpoint-url">
                  {recordingEndpointUrl}
                </code>
              </div>

              <div className="webhooks-modal__section">
                <h4 className="webhooks-modal__section-title">Trigger a recording (POST)</h4>
                <p className="webhooks-modal__description">
                  Send a POST with a <code>requestId</code> from the <code>backend_requests</code> table.
                </p>
                <pre className="webhooks-modal__code-block" data-testid="webhooks-post-example">
{`curl -X POST ${recordingEndpointUrl} \\
  -H "Content-Type: application/json" \\
  -d '{"requestId": "<REQUEST_ID>"}'`}
                </pre>
              </div>

              <div className="webhooks-modal__section">
                <h4 className="webhooks-modal__section-title">Check recording status (GET)</h4>
                <pre className="webhooks-modal__code-block" data-testid="webhooks-get-example">
{`curl ${recordingEndpointUrl}?requestId=<REQUEST_ID>`}
                </pre>
              </div>

              <div className="webhooks-modal__section">
                <h4 className="webhooks-modal__section-title">Response statuses</h4>
                <table className="webhooks-modal__table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>HTTP</th>
                      <th>Meaning</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td><code>recorded</code></td><td>200</td><td>Recording exists &mdash; <code>recordingId</code> included</td></tr>
                    <tr><td><code>pending</code></td><td>200</td><td>Recording queued or processing</td></tr>
                    <tr><td><code>queued</code></td><td>202</td><td>Recording just queued by this POST</td></tr>
                    <tr><td><code>not_found</code></td><td>404</td><td>Request ID not in backend_requests</td></tr>
                    <tr><td><code>error</code></td><td>4xx/5xx</td><td>Validation or recording failure</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="webhooks-modal__actions">
          <button
            className="webhooks-modal__btn webhooks-modal__btn--close"
            onClick={onClose}
            data-testid="webhooks-modal-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default WebhooksModal
